import { use, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { MultiPageDialog } from '~components';
import SelectedKeysContext from '~features/keys/providers/selectedKeysContext';
import Organisation from '~interfaces/organisation';
import ResourceGroup from '~interfaces/resourceGroup';

import SelectedKeysOverview from '../forms/selectedKeysOverview';
import SelectOrganisationAndResourceGroupForm from '../forms/selectOrganisationAndResourceGroupForm';

interface Props {
	open: boolean;
	loading?: boolean;
	onClose: () => void;
	onFinish: (
		organisationId: string,
		resourceGroupId: string | undefined,
		closeDialog: () => void
	) => void;
	currentlyAssignedOrganisation?: Organisation | undefined;
	showEditOrganisation?: boolean;
}

const KeyMultiAssignDialog = ({
	open,
	loading = false,
	onClose,
	onFinish,
	currentlyAssignedOrganisation,
	showEditOrganisation = true,
}: Props) => {
	const { t } = useTranslation();
	const selectedKeysContext = use(SelectedKeysContext);

	const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | undefined>(
		undefined
	);
	const [selectedResourceGroup, setSelectedResourceGroup] = useState<ResourceGroup | undefined>(
		undefined
	);
	const effectiveOrganisation =
		showEditOrganisation ? selectedOrganisation : currentlyAssignedOrganisation;
	const isChangeResourceGroup = !showEditOrganisation;

	const subtitle = useMemo(() => {
		const entries = [];
		if (effectiveOrganisation) entries.push(effectiveOrganisation.name);
		if (selectedResourceGroup) entries.push(selectedResourceGroup.name);
		return entries.join(' > ');
	}, [effectiveOrganisation, selectedResourceGroup]);

	const reset = () => {
		setSelectedOrganisation(undefined);
		setSelectedResourceGroup(undefined);
	};

	const title =
		isChangeResourceGroup ? t('changeResourceGroup', { ns: 'actions' })
		: selectedKeysContext.singleKey ? t('key.labels.assignKey', { ns: 'entities' })
		: t('key.labels.multiAssignKeys', { ns: 'entities' });

	return (
		<MultiPageDialog
			title={title}
			open={open}
			loading={loading}
			subtitle={subtitle}
			onClose={() => {
				onClose();
				reset();
			}}
			onFinish={() => {
				if (effectiveOrganisation) {
					onFinish(effectiveOrganisation.id, selectedResourceGroup?.id, () => {
						reset();
					});
				}
			}}
			pages={[
				{
					key: 'assignForm',
					content: (
						<SelectOrganisationAndResourceGroupForm
							organisation={effectiveOrganisation}
							showEditOrganisation={showEditOrganisation}
							resourceGroup={selectedResourceGroup}
							resourceGroupDisabled={effectiveOrganisation === undefined}
							onValuesChanged={(newValues) => {
								setSelectedOrganisation(newValues.organisation);
								setSelectedResourceGroup(newValues.resourceGroup);
							}}
						/>
					),
					enableNext: effectiveOrganisation !== undefined,
				},
				{
					key: 'selectedKeys',
					content: (
						<SelectedKeysOverview
							selectedKeys={
								selectedKeysContext.singleKey ?
									[selectedKeysContext.singleKey]
								:	selectedKeysContext.selectedKeys
							}
							onRemoveClicked={selectedKeysContext.removeKey}
						/>
					),
				},
			]}
		/>
	);
};

export default KeyMultiAssignDialog;
